"use client";

import * as React from "react";
import { LegalObligationsCombobox } from "@/components/ui/LegalObligationsCombobox";
import { LegalObligationsProvider } from "@/components/ui/LegalObligationsCombobox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const LegalObligationsComboboxExample = () => {
  const [selectedObligations, setSelectedObligations] = React.useState<string[]>([]);
  const handleSelect = (id: string) => {
    toast.success("Obligation selected", {
      description: `Selected obligation with ID: ${id}`,
    });
  };

  const handleDeselect = (id: string) => {
    toast.error("Obligation deselected", {
      description: `Deselected obligation with ID: ${id}`,
    });
  };

  return (
    <LegalObligationsProvider>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Legal Obligations Combobox</h2>
          <p className="text-muted-foreground">
            A combobox component for selecting legal obligations with search and multi-select capabilities.
          </p>
        </div>

        <Tabs defaultValue="preview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="preview">Preview</TabsTrigger>
            <TabsTrigger value="code">Code</TabsTrigger>
          </TabsList>
          
          <TabsContent value="preview" className="space-y-4">
            <div className="rounded-md border p-4">
              <div className="grid gap-6">
                <div className="space-y-2">
                  <h3 className="text-lg font-medium">Basic Usage</h3>
                  <LegalObligationsCombobox
                    selected={selectedObligations}
                    setSelected={setSelectedObligations}
                    onSelect={handleSelect}
                    onDeselect={handleDeselect}
                    placeholder="Select legal obligations..."
                    className="w-full max-w-md"
                  />
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-medium">Selected Obligations</h3>
                  <div className="rounded-md border p-4">
                    {selectedObligations.length > 0 ? (
                      <ul className="space-y-2">
                        {selectedObligations.map((id) => (
                          <li key={id} className="flex items-center justify-between py-1">
                            <span>Obligation ID: {id}</span>
                            <button
                              onClick={() => {
                                setSelectedObligations(prev => 
                                  prev.filter(item => item !== id)
                                );
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted-foreground">No obligations selected</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="code">
            <Card>
              <CardHeader>
                <CardTitle>Implementation</CardTitle>
                <CardDescription>
                  Here's how to implement the LegalObligationsCombobox in your application.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted rounded-md p-4 overflow-x-auto text-sm">
                  {`import { useState } from "react";
import { LegalObligationsCombobox, LegalObligationsProvider } from "@/components/ui/LegalObligationsCombobox";

export function YourComponent() {
  const [selectedObligations, setSelectedObligations] = useState<string[]>([]);

  return (
    <LegalObligationsProvider>
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Legal Obligations</h3>
        <LegalObligationsCombobox
          selected={selectedObligations}
          setSelected={setSelectedObligations}
          placeholder="Select legal obligations..."
          className="w-full max-w-md"
        />
        
        {/* Display selected obligations */}
        <div>
          <h4 className="font-medium mb-2">Selected Obligations:</h4>
          {selectedObligations.length > 0 ? (
            <ul className="space-y-1">
              {selectedObligations.map((id) => (
                <li key={id}>Obligation ID: {id}</li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground">No obligations selected</p>
          )}
        </div>
      </div>
    </LegalObligationsProvider>
  );
}`}
                </pre>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </LegalObligationsProvider>
  );
};

export default LegalObligationsComboboxExample;
